/**
 * FullCalendar integration utilities
 */

import type { EventInput } from "@fullcalendar/core";

import type { ProcessedEvent } from "@/types/calendar";

export const convertToFullCalendarEvent = (
  event: ProcessedEvent,
): EventInput => {
  return {
    id: event.id,
    title: event.title,
    start: event.start,
    end: event.end,
    extendedProps: {
      description: event.description,
      location: event.location,
      meetingLink: event.meetingLink,
      attendees: event.attendees,
      originalEvent: event,
    },
    classNames: getEventClassNames(event),
  };
};

export const convertMultipleToFullCalendarEvents = (
  events: ProcessedEvent[],
): EventInput[] => {
  return events.map(convertToFullCalendarEvent);
};

export const getEventClassNames = (event: ProcessedEvent): string[] => {
  const classNames: string[] = [];

  // Add class based on meeting type
  if (event.meetingLink) {
    classNames.push("fc-event-virtual");
  } else {
    classNames.push("fc-event-in-person");
  }

  // Add class based on attendee count
  if (event.attendees) {
    if (event.attendees.length > 10) {
      classNames.push("fc-event-extra-large");
    } else if (event.attendees.length > 5) {
      classNames.push("fc-event-large");
    } else if (event.attendees.length === 1) {
      classNames.push("fc-event-solo");
    }
  }

  // Add status class
  const now = new Date();
  const start = new Date(event.start);
  const end = new Date(event.end);

  if (start <= now && end >= now) {
    classNames.push("fc-event-happening-now");
  } else if (start > now) {
    classNames.push("fc-event-upcoming");
  } else {
    classNames.push("fc-event-past");
  }

  // Add priority class if available
  if (event.priority) {
    classNames.push(`fc-event-priority-${event.priority}`);
  }

  // Add category class if available
  if (event.category) {
    classNames.push(
      `fc-event-category-${event.category.toLowerCase().replace(/\s+/g, "-")}`,
    );
  }

  return classNames.filter(Boolean);
};

export const getCalendarViewMapping = (view: string): string => {
  const viewMap: Record<string, string> = {
    month: "dayGridMonth",
    week: "timeGridWeek",
    day: "timeGridDay",
    list: "listWeek",
    year: "multiMonthYear",
  };

  return viewMap[view] || "dayGridMonth";
};

export const getCalendarConfig = () => {
  return {
    initialView: "dayGridMonth",
    headerToolbar: {
      left: "prev,next today",
      center: "title",
      right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
    },
    plugins: [],
    height: "100%",
    contentHeight: "auto",
    aspectRatio: 1.8,
    weekends: true,
    editable: false,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: 3,
    eventMaxStack: 3,
    nowIndicator: true,
    slotMinTime: "06:00:00",
    slotMaxTime: "22:00:00",
    slotDuration: "00:30:00",
    slotLabelInterval: "01:00:00",
    scrollTime: "08:00:00",
    businessHours: {
      daysOfWeek: [1, 2, 3, 4, 5],
      startTime: "09:00",
      endTime: "18:00",
    },
    eventDisplay: "block",
    eventTimeFormat: {
      hour: "numeric",
      minute: "2-digit",
      meridiem: "short",
    },
    dayHeaderFormat: {
      weekday: "short",
      month: "numeric",
      day: "numeric",
      omitCommas: true,
    },
    views: {
      dayGrid: {
        dayMaxEventRows: 3,
      },
      timeGrid: {
        dayMaxEventRows: 6,
      },
    },
  };
};

export const getEventColor = (event: ProcessedEvent): string => {
  // Define color scheme based on event properties
  if (event.category) {
    const categoryColors: Record<string, string> = {
      meeting: "#3788d8",
      deadline: "#e74c3c",
      milestone: "#f39c12",
      review: "#9b59b6",
      training: "#2ecc71",
      social: "#1abc9c",
      other: "#95a5a6",
    };
    return categoryColors[event.category.toLowerCase()] || "#3788d8";
  }

  // Default color based on time
  const now = new Date();
  const start = new Date(event.start);

  if (start < now) {
    return "#95a5a6"; // Gray for past events
  } else if (start.getTime() - now.getTime() < 24 * 60 * 60 * 1000) {
    return "#e74c3c"; // Red for events within 24 hours
  } else {
    return "#3788d8"; // Blue for future events
  }
};
